/**
 * Modal de detalle de una orden (`GET /api/v1/orders/<id>`, vista detalle).
 *
 * Replica el "modal de detalles" del SSR (`progress/explore_orders_ssr.md`): orden,
 * cliente, tipo, estado, estado de pago, montos, atendido, creado por, fechas y
 * timestamps de ciclo de vida. Carga el detalle con `useOrder` (TanStack Query) y
 * ofrece las acciones de transición (`OrderActions`) desde el propio detalle.
 *
 * Controlado por el `orderId` del padre (la lista): con `null`, el modal está
 * cerrado y la query inactiva. Presentacional + un hook de datos; sin `fetch`.
 */
import { Link } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { orderEditPath } from "@/features/navigation";

import { formatAmount, formatDate, formatDateTime } from "../format";
import { useOrder } from "../hooks/useOrder";
import { OrderActions } from "./OrderActions";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { PaymentBadge } from "./PaymentBadge";

interface OrderDetailDialogProps {
  /** Orden a mostrar; `null` mantiene el modal cerrado. */
  orderId: number | null;
  onClose: () => void;
}

/** Fila etiqueta/valor del detalle. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{children}</dd>
    </div>
  );
}

export function OrderDetailDialog({ orderId, onClose }: OrderDetailDialogProps) {
  const { data, isPending, isError, error } = useOrder(orderId);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("edit_orders");

  return (
    <Dialog
      open={orderId !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-w-lg"
        aria-describedby={undefined}
      >
        <DialogTitle>
          {data ? `Orden ${data.order_number}` : "Detalle de la orden"}
        </DialogTitle>

        {orderId !== null && isPending ? (
          <p className="mt-4 text-sm text-slate-500" role="status">
            Cargando detalle…
          </p>
        ) : isError ? (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error instanceof Error && error.message
              ? error.message
              : "No se pudo cargar el detalle."}
          </p>
        ) : data ? (
          <>
            <dl className="mt-4 divide-y divide-slate-100">
              <DetailRow label="Cliente">{data.customer_name}</DetailRow>
              <DetailRow label="Tipo">{data.order_type}</DetailRow>
              <DetailRow label="Estado">
                <OrderStatusBadge status={data.status} />
              </DetailRow>
              <DetailRow label="Estado de pago">
                <PaymentBadge payment={data.payment_status} />
              </DetailRow>
              <DetailRow label="Monto">{formatAmount(data.sale_amount)}</DetailRow>
              <DetailRow label="Abonado">{formatAmount(data.amount_paid)}</DetailRow>
              <DetailRow label="Pendiente">
                {formatAmount(data.remaining_amount)}
              </DetailRow>
              <DetailRow label="Atendido por">{data.attendant}</DetailRow>
              <DetailRow label="Creado por">{data.created_by ?? "—"}</DetailRow>
              <DetailRow label="F. ingreso">{formatDate(data.invoice_date)}</DetailRow>
              <DetailRow label="F. entrega">{formatDate(data.delivery_date)}</DetailRow>
              <DetailRow label="Hora">{data.delivery_time ?? "—"}</DetailRow>
              <DetailRow label="Completada">
                {formatDateTime(data.completed_at)}
              </DetailRow>
              <DetailRow label="Entregada">
                {formatDateTime(data.delivered_at)}
              </DetailRow>
              {data.description ? (
                <DetailRow label="Descripción">{data.description}</DetailRow>
              ) : null}
            </dl>

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
              {canEdit && (
                <Button asChild variant="secondary" size="md">
                  <Link to={orderEditPath(data.id)} onClick={onClose}>
                    Editar
                  </Link>
                </Button>
              )}
              <OrderActions
                orderId={data.id}
                orderNumber={data.order_number}
                status={data.status}
                paymentStatus={data.payment_status}
                size="md"
              />
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
